// ================================================================
// 大師個案列表頁面（Dashboard）
// ================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  AppBar,
  Toolbar,
  InputAdornment,
} from '@mui/material';
import {
  AutoAwesome,
  Search,
  PersonAdd,
  Logout,
  Person,
  Female,
  Male,
  HistoryEdu,
  Lock,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useTeacher } from '../../contexts/TeacherContext';
import { getConsultees, createConsultee, Consultee, teacherChangePassword } from '../../api/teacher';

// ================================================================
// 出生年月日時 選項
// ================================================================

const YEARS = Array.from({ length: 78 }, (_, i) => 2019 - i); // 1942~2019
const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);
const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);
// 13 個選項：子時分兩段（00:01-01:00 需轉前一天；兩者皆存 hh='23'）
// hh 值依據 0_PreLoad.r：WestHour=23→子時，WestHour=1→丑時，…，WestHour=21→亥時
interface HourOption { value: string; label: string; hourCode: string; prevDay: boolean; }
const HOURS: HourOption[] = [
  { value: '23_prev', label: '子時 (00:01-01:00)',  hourCode: '23', prevDay: true  },
  { value: '01',      label: '丑時 (01:00-03:00)',  hourCode: '01', prevDay: false },
  { value: '03',      label: '寅時 (03:00-05:00)',  hourCode: '03', prevDay: false },
  { value: '05',      label: '卯時 (05:00-07:00)',  hourCode: '05', prevDay: false },
  { value: '07',      label: '辰時 (07:00-09:00)',  hourCode: '07', prevDay: false },
  { value: '09',      label: '巳時 (09:00-11:00)',  hourCode: '09', prevDay: false },
  { value: '11',      label: '午時 (11:00-13:00)',  hourCode: '11', prevDay: false },
  { value: '13',      label: '未時 (13:00-15:00)',  hourCode: '13', prevDay: false },
  { value: '15',      label: '申時 (15:00-17:00)',  hourCode: '15', prevDay: false },
  { value: '17',      label: '酉時 (17:00-19:00)',  hourCode: '17', prevDay: false },
  { value: '19',      label: '戌時 (19:00-21:00)',  hourCode: '19', prevDay: false },
  { value: '21',      label: '亥時 (21:00-23:00)',  hourCode: '21', prevDay: false },
  { value: '23',      label: '子時 (23:01-24:00)',  hourCode: '23', prevDay: false },
];

// ================================================================
// 輔助函數
// ================================================================

function getShiChenName(hourCode: string): string {
  // 由 birth_datetime 取出的 hh 永遠是 '01'/'03'/.../'23'，用 hourCode 對應
  const found = HOURS.find((h) => h.hourCode === hourCode);
  return found ? found.label.split(' ')[0] : hourCode;
}

function formatBirthDate(birthDatetime: string): string {
  if (!birthDatetime || birthDatetime.length < 8) return '';
  const y = birthDatetime.substring(0, 4);
  const m = parseInt(birthDatetime.substring(4, 6), 10);
  const d = parseInt(birthDatetime.substring(6, 8), 10);
  const hCode = birthDatetime.length >= 10 ? birthDatetime.substring(8, 10) : null;
  const hourLabel = hCode ? ` ${getShiChenName(hCode)}` : '';
  return `${y}/${m}/${d}${hourLabel}`;
}

function buildBirthDatetime(year: number, month: number, day: number, hourValue: string): string {
  const opt = HOURS.find((h) => h.value === hourValue);
  const hourCode = opt ? opt.hourCode : hourValue;
  const prevDay = opt ? opt.prevDay : false;
  // 子時 (00:01-01:00) 需往前推一天（中式算法：子時從前日 23 時起算）
  const d = new Date(year, month - 1, day);
  if (prevDay) d.setDate(d.getDate() - 1);
  const y = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}${mm}${dd}${hourCode}`;
}

// ================================================================
// 新增個案對話框
// ================================================================

interface AddConsulteeDialogProps {
  open: boolean;
  onClose: () => void;
  onAdded: () => void;
}

const AddConsulteeDialog: React.FC<AddConsulteeDialogProps> = ({
  open,
  onClose,
  onAdded,
}) => {
  const [name, setName] = useState('');
  const [gender, setGender] = useState<number>(1);
  const [year, setYear] = useState<number>(1985);
  const [month, setMonth] = useState<number>(1);
  const [day, setDay] = useState<number>(1);
  const [hour, setHour] = useState<string>('11'); // 午時 (hh='11', 11:00-13:00)
  const [notes, setNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setName('');
    setGender(1);
    setYear(1985);
    setMonth(1);
    setDay(1);
    setHour('11');
    setNotes('');
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError('請輸入姓名或代稱');
      return;
    }
    setError(null);
    setIsLoading(true);
    try {
      const birth_datetime = buildBirthDatetime(year, month, day, hour);
      const res = await createConsultee({
        name: name.trim(),
        gender,
        birth_datetime,
        notes: notes.trim() || undefined,
      });
      if (!res.success) {
        setError(res.message || '新增失敗');
        return;
      }
      onAdded();
      handleClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || '新增失敗');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>新增個案</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          label="姓名或代稱"
          value={name}
          onChange={(e) => setName(e.target.value)}
          margin="normal"
          required
          autoFocus
        />

        <FormControl fullWidth margin="normal">
          <InputLabel>性別</InputLabel>
          <Select
            value={gender}
            label="性別"
            onChange={(e) => setGender(Number(e.target.value))}
          >
            <MenuItem value={1}>男</MenuItem>
            <MenuItem value={0}>女</MenuItem>
          </Select>
        </FormControl>

        <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
          出生年月日時
        </Typography>

        <Grid container spacing={1}>
          <Grid item xs={3}>
            <FormControl fullWidth size="small">
              <InputLabel>年</InputLabel>
              <Select
                value={year}
                label="年"
                onChange={(e) => setYear(Number(e.target.value))}
                MenuProps={{ PaperProps: { style: { maxHeight: 200 } } }}
              >
                {YEARS.map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <FormControl fullWidth size="small">
              <InputLabel>月</InputLabel>
              <Select
                value={month}
                label="月"
                onChange={(e) => setMonth(Number(e.target.value))}
              >
                {MONTHS.map((m) => (
                  <MenuItem key={m} value={m}>{m}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <FormControl fullWidth size="small">
              <InputLabel>日</InputLabel>
              <Select
                value={day}
                label="日"
                onChange={(e) => setDay(Number(e.target.value))}
              >
                {DAYS.map((d) => (
                  <MenuItem key={d} value={d}>{d}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <FormControl fullWidth size="small">
              <InputLabel>時</InputLabel>
              <Select
                value={hour}
                label="時"
                onChange={(e) => setHour(e.target.value as string)}
                MenuProps={{ PaperProps: { style: { maxHeight: 200 } } }}
              >
                {HOURS.map((h) => (
                  <MenuItem key={h.value} value={h.value}>{h.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <TextField
          fullWidth
          label="備注（選用）"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          margin="normal"
          multiline
          rows={2}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isLoading}>
          取消
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="secondary"
          disabled={isLoading || !name.trim()}
        >
          {isLoading ? <CircularProgress size={20} color="inherit" /> : '新增'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ================================================================
// 個案卡片
// ================================================================

interface ConsulteeCardProps {
  consultee: Consultee;
  onClick: () => void;
}

const ConsulteeCard: React.FC<ConsulteeCardProps> = ({ consultee, onClick }) => {
  return (
    <Card
      sx={{
        height: '100%',
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: 4 },
      }}
    >
      <CardActionArea onClick={onClick} sx={{ height: '100%' }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            {consultee.gender === 1 ? (
              <Male sx={{ color: 'primary.main' }} />
            ) : (
              <Female sx={{ color: 'secondary.main' }} />
            )}
            <Typography variant="h6" fontWeight="bold" noWrap>
              {consultee.name}
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            {formatBirthDate(consultee.birth_datetime)}
          </Typography>

          {consultee.notes && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {consultee.notes}
            </Typography>
          )}

          <Box mt={2}>
            <Chip
              icon={<HistoryEdu fontSize="small" />}
              label={`${consultee.consultation_count} 次諮詢`}
              size="small"
              variant="outlined"
              color={consultee.consultation_count > 0 ? 'secondary' : 'default'}
            />
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

// ================================================================
// 主頁面
// ================================================================

const TeacherDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { teacher, logout } = useTeacher();

  const [consultees, setConsultees] = useState<Consultee[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  // 修改密碼 Dialog 狀態
  const [pwDialogOpen, setPwDialogOpen] = useState(false);
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwSuccess, setPwSuccess] = useState(false);
  const [pwShowNew, setPwShowNew] = useState(false);
  const [pwShowConfirm, setPwShowConfirm] = useState(false);

  const handleOpenPwDialog = () => {
    setPwCurrent(''); setPwNew(''); setPwConfirm('');
    setPwError(null); setPwSuccess(false);
    setPwDialogOpen(true);
  };
  const handleClosePwDialog = () => { if (!pwLoading) setPwDialogOpen(false); };

  const handleChangePassword = async () => {
    setPwError(null);
    if (!pwCurrent || !pwNew || !pwConfirm) {
      setPwError('請填寫所有欄位'); return;
    }
    if (pwNew.length < 6) {
      setPwError('新密碼長度至少需要 6 個字元'); return;
    }
    if (pwNew !== pwConfirm) {
      setPwError('兩次輸入的新密碼不一致'); return;
    }
    setPwLoading(true);
    try {
      const res = await teacherChangePassword(pwCurrent, pwNew);
      if (res.success) {
        setPwSuccess(true);
        setTimeout(() => setPwDialogOpen(false), 1500);
      } else {
        setPwError(res.message || '修改失敗');
      }
    } catch (err: any) {
      setPwError(err?.response?.data?.message || err.message || '修改失敗');
    } finally {
      setPwLoading(false);
    }
  };

  const fetchConsultees = useCallback(async (q?: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await getConsultees(q || undefined);
      if (res.success) {
        setConsultees(res.consultees || []);
      } else {
        setError(res.message || '載入失敗');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || '載入失敗');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConsultees();
  }, [fetchConsultees]);

  const handleSearch = () => {
    setSearch(searchInput);
    fetchConsultees(searchInput || undefined);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleSearchClear = () => {
    setSearchInput('');
    setSearch('');
    fetchConsultees();
  };

  const handleAddDialogClose = () => setAddDialogOpen(false);

  const handleConsulteeAdded = () => {
    fetchConsultees(search || undefined);
  };

  const handleLogout = () => {
    logout();
    navigate('/teacher/login');
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      {/* AppBar */}
      <AppBar
        position="sticky"
        sx={{
          background: 'linear-gradient(135deg, #2d1b69 0%, #7b2d8b 100%)',
        }}
        elevation={2}
      >
        <Toolbar>
          <AutoAwesome sx={{ mr: 1.5 }} />
          <Typography variant="h6" fontWeight="bold" sx={{ flexGrow: 1 }}>
            大師諮詢系統
          </Typography>
          {teacher && (
            <Box display="flex" alignItems="center" gap={1}>
              <Person fontSize="small" />
              <Typography variant="body2" sx={{ mr: 1 }}>
                大師模式 — {teacher.display_name || teacher.username}
              </Typography>
            </Box>
          )}
          <Button
            color="inherit"
            onClick={handleOpenPwDialog}
            startIcon={<Lock />}
            size="small"
            sx={{ mr: 0.5 }}
          >
            修改密碼
          </Button>
          <IconButton color="inherit" onClick={handleLogout} title="登出">
            <Logout />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* 頁面標題列 */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
          flexWrap="wrap"
          gap={2}
        >
          <Typography variant="h5" fontWeight="bold">
            個案列表
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<PersonAdd />}
            onClick={() => setAddDialogOpen(true)}
          >
            新增個案
          </Button>
        </Box>

        {/* 搜尋列 */}
        <Box display="flex" gap={1} mb={3}>
          <TextField
            fullWidth
            placeholder="搜尋姓名或備注..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Button variant="outlined" onClick={handleSearch}>
            搜尋
          </Button>
          {search && (
            <Button variant="text" onClick={handleSearchClear}>
              清除
            </Button>
          )}
        </Box>

        {/* 錯誤訊息 */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* 載入中 */}
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress color="secondary" />
          </Box>
        ) : consultees.length === 0 ? (
          /* 空狀態 */
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={10}
            color="text.secondary"
          >
            <Person sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
            <Typography variant="h6" gutterBottom>
              {search ? `找不到「${search}」的個案` : '尚無個案'}
            </Typography>
            <Typography variant="body2" mb={3}>
              {search ? '請嘗試其他關鍵字' : '點擊「新增個案」開始記錄諮詢'}
            </Typography>
            {!search && (
              <Button
                variant="contained"
                color="secondary"
                startIcon={<PersonAdd />}
                onClick={() => setAddDialogOpen(true)}
              >
                新增個案
              </Button>
            )}
          </Box>
        ) : (
          /* 個案列表 */
          <>
            <Typography variant="body2" color="text.secondary" mb={2}>
              共 {consultees.length} 位個案
              {search && `（搜尋：${search}）`}
            </Typography>
            <Grid container spacing={2}>
              {consultees.map((c) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={c.id}>
                  <ConsulteeCard
                    consultee={c}
                    onClick={() => navigate(`/teacher/consultees/${c.id}`)}
                  />
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Container>

      {/* 新增個案對話框 */}
      <AddConsulteeDialog
        open={addDialogOpen}
        onClose={handleAddDialogClose}
        onAdded={handleConsulteeAdded}
      />

      {/* 修改密碼對話框 */}
      <Dialog open={pwDialogOpen} onClose={handleClosePwDialog} maxWidth="xs" fullWidth>
        <DialogTitle>修改密碼</DialogTitle>
        <DialogContent>
          {pwSuccess ? (
            <Alert severity="success" sx={{ mt: 1 }}>密碼修改成功！</Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {pwError && <Alert severity="error">{pwError}</Alert>}
              <TextField
                label="當前密碼"
                type="password"
                value={pwCurrent}
                onChange={(e) => setPwCurrent(e.target.value)}
                disabled={pwLoading}
                fullWidth
              />
              <TextField
                label="新密碼（至少 6 字元）"
                type={pwShowNew ? 'text' : 'password'}
                value={pwNew}
                onChange={(e) => setPwNew(e.target.value)}
                disabled={pwLoading}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setPwShowNew((v) => !v)}
                        edge="end"
                        tabIndex={-1}
                      >
                        {pwShowNew ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="確認新密碼"
                type={pwShowConfirm ? 'text' : 'password'}
                value={pwConfirm}
                onChange={(e) => setPwConfirm(e.target.value)}
                disabled={pwLoading}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setPwShowConfirm((v) => !v)}
                        edge="end"
                        tabIndex={-1}
                      >
                        {pwShowConfirm ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePwDialog} disabled={pwLoading}>取消</Button>
          {!pwSuccess && (
            <Button
              variant="contained"
              onClick={handleChangePassword}
              disabled={pwLoading}
            >
              {pwLoading ? <CircularProgress size={20} /> : '確認修改'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeacherDashboardPage;
